import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Users as UsersIcon, Plus, CheckCircle, XCircle, Search } from 'lucide-react';

interface User {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    active: boolean | number;
    created_at: string;
}

const UsersManagement = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'caissier'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des utilisateurs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id: number, currentStatus: boolean | number) => {
        try {
            const isActive = currentStatus === 1 || currentStatus === true;
            if (isActive) {
                await api.put(`/users/${id}/deactivate`);
            } else {
                await api.put(`/users/${id}/activate`);
            }
            fetchUsers(); // Recharger la liste
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
            alert("Erreur lors de la modification de l'utilisateur.");
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/users', formData);
            setShowModal(false);
            setFormData({ first_name: '', last_name: '', email: '', password: '', role: 'caissier' });
            fetchUsers();
        } catch (error) {
            console.error('Erreur lors de la création:', error);
            alert("Erreur lors de la création de l'utilisateur (peut-être l'email existe déjà ?).");
        }
    };

    const filteredUsers = users.filter(user => {
        const search = searchTerm.toLowerCase();
        return (
            user.first_name.toLowerCase().includes(search) || 
            user.last_name.toLowerCase().includes(search) ||
            user.email.toLowerCase().includes(search)
        );
    });

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <UsersIcon className="text-brand-primary" />
                            Gestion des Utilisateurs
                        </h1>
                        <p className="text-gray-500 mt-1 text-sm">
                            Gérez les accès, créez et désactivez les comptes admin et caissiers.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <input
                                type="text"
                                placeholder="Rechercher..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        </div>
                        <button
                            onClick={() => setShowModal(true)}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary text-brand-dark rounded-md hover:bg-brand-accent transition-colors shadow-sm w-full sm:w-auto"
                        >
                            <Plus size={18} />
                            Nouvel Utilisateur
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 border-b border-gray-200 text-sm uppercase tracking-wider">
                                    <th className="p-4 font-semibold">Nom Complet</th>
                                    <th className="p-4 font-semibold">Email</th>
                                    <th className="p-4 font-semibold">Rôle</th>
                                    <th className="p-4 font-semibold text-center">Statut</th>
                                    <th className="p-4 font-semibold text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredUsers.map((user) => {
                                    const isActive = user.active === 1 || user.active === true;
                                    return (
                                        <tr key={user.id} className={`hover:bg-gray-50 transition-colors ${!isActive ? 'opacity-60' : ''}`}>
                                            <td className="p-4 text-sm font-medium text-gray-800">
                                                {user.first_name} {user.last_name}
                                            </td>
                                            <td className="p-4 text-sm text-gray-600">{user.email}</td>
                                            <td className="p-4 text-sm text-gray-500 capitalize">{user.role}</td>
                                            <td className="p-4 text-center">
                                                {isActive ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <CheckCircle size={12} /> Actif
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        <XCircle size={12} /> Inactif
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-4 text-center">
                                                <button
                                                    onClick={() => handleToggleStatus(user.id, user.active)}
                                                    className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors border ${
                                                        isActive 
                                                        ? 'border-red-200 text-red-600 hover:bg-red-50' 
                                                        : 'border-green-200 text-green-600 hover:bg-green-50'
                                                    }`}
                                                >
                                                    {isActive ? 'Désactiver' : 'Activer'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-gray-500">
                                            Aucun utilisateur trouvé.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de création */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-800">Créer un utilisateur</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <XCircle size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                                    <input 
                                        type="text" required 
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none"
                                        value={formData.first_name}
                                        onChange={e => setFormData({...formData, first_name: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                                    <input 
                                        type="text" required 
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none"
                                        value={formData.last_name}
                                        onChange={e => setFormData({...formData, last_name: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input 
                                    type="email" required 
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none"
                                    value={formData.email}
                                    onChange={e => setFormData({...formData, email: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                                <input 
                                    type="password" required 
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none"
                                    value={formData.password}
                                    onChange={e => setFormData({...formData, password: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                                <select 
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none"
                                    value={formData.role}
                                    onChange={e => setFormData({...formData, role: e.target.value})}
                                >
                                    <option value="caissier">Caissier</option>
                                    <option value="admin">Administrateur</option>
                                </select>
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button 
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Annuler
                                </button>
                                <button 
                                    type="submit"
                                    className="flex-1 py-2 px-4 bg-brand-primary text-brand-dark rounded hover:bg-brand-accent transition-colors font-medium"
                                >
                                    Créer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersManagement;
